/**
 * Карточка смены.
 *
 * У няни это главная кнопка приложения, поэтому она большая, одна и всегда
 * на одном месте. Пока смена идёт — карточка показывает живой счётчик и
 * накопленную сумму: няня в любой момент видит, сколько уже заработала,
 * и ей не нужно ничего считать в голове.
 */

import { useEffect, useState } from 'react'

import { GeoLine } from '@/features/geo/GeoLine'
import type { GeoPoint, Shift } from '@/shared/api/day'
import { formatDuration, formatTime } from '@/shared/format'
import { Button } from '@/shared/ui/Button'
import { Card, Chip, Money } from '@/shared/ui/primitives'

function useLiveMinutes(from: string | null | undefined): number | null {
  const [minutes, setMinutes] = useState<number | null>(null)

  useEffect(() => {
    if (!from) {
      setMinutes(null)
      return
    }
    const tick = () =>
      setMinutes(Math.floor((Date.now() - new Date(from).getTime()) / 60_000))
    tick()
    // раз в 30 секунд: минуты меняются медленно, а батарею беречь надо
    const timer = window.setInterval(tick, 30_000)
    return () => window.clearInterval(timer)
  }, [from])

  return minutes
}

export function NannyShiftCard({
  shift,
  minutesSoFar,
  amountSoFar,
  currency,
  timeZone,
  starting,
  onStart,
  onFinish,
}: {
  shift?: Shift | null
  minutesSoFar?: number | null
  amountSoFar?: string | null
  currency: string
  timeZone: string
  starting: boolean
  onStart: () => void
  onFinish: () => void
}) {
  const live = useLiveMinutes(shift?.status === 'active' ? shift.actual_start : null)

  if (!shift || shift.status !== 'active') {
    return (
      <Card className="overflow-hidden text-center">
        <p className="font-display text-title leading-tight">День ещё не начался</p>
        <p className="font-hand text-text-muted mt-1 text-lead leading-tight">
          нажмите, когда будете на месте
        </p>
        <Button
          className="mt-4"
          block
          size="lg"
          loading={starting}
          hapticKind="success"
          onClick={onStart}
        >
          Начать смену
        </Button>
      </Card>
    )
  }

  const minutes = live ?? minutesSoFar ?? 0

  return (
    <Card className="bg-accent relative overflow-hidden border-transparent text-on-accent shadow-[var(--shadow-raised)]">
      {/* тёплый блик из левого верхнего угла: плоская заливка на бумаге
          выглядит вырезанной дырой, а блик возвращает карточке объём */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-16 -left-10 size-56 rounded-full bg-on-accent/12 blur-2xl"
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Chip tone="neutral" dot className="bg-on-accent/15 text-on-accent">
            смена идёт
          </Chip>
          <p className="font-display tabular mt-2 text-hero leading-none">
            {formatDuration(minutes)}
          </p>
          <p className="mt-1 text-caption text-on-accent/85">
            с {shift.actual_start ? formatTime(shift.actual_start, timeZone) : '—'}
          </p>
        </div>
        {amountSoFar && (
          <div className="text-right">
            <p className="text-caption text-on-accent/85">заработано</p>
            <Money value={amountSoFar} currency={currency} size="lg" className="text-on-accent" />
          </div>
        )}
      </div>

      <Button
        className="relative mt-4 border-on-accent/30 bg-on-accent/20 text-on-accent shadow-none active:bg-on-accent/30"
        block
        size="lg"
        variant="secondary"
        onClick={onFinish}
      >
        Завершить смену
      </Button>
    </Card>
  )
}

export function ParentShiftCard({
  shift,
  minutesSoFar,
  currency,
  timeZone,
  nannyName,
  onGeoPress,
}: {
  shift?: Shift | null
  minutesSoFar?: number | null
  currency: string
  timeZone: string
  nannyName: string
  /** Точки начала и конца смены открываются на той же карте, что и события. */
  onGeoPress?: (geo: GeoPoint) => void
}) {
  const live = useLiveMinutes(shift?.status === 'active' ? shift.actual_start : null)

  if (!shift) {
    return (
      <Card>
        <p className="text-text-muted text-note">
          Смена ещё не началась. Как только {nannyName} нажмёт «Начать смену», здесь появится
          живой день ребёнка.
        </p>
      </Card>
    )
  }

  if (shift.status === 'active') {
    const minutes = live ?? minutesSoFar ?? 0
    return (
      <Card>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <Chip tone="accent" dot>
              {nannyName} на смене
            </Chip>
            <p className="text-text-muted mt-2 text-note">
              с {shift.actual_start ? formatTime(shift.actual_start, timeZone) : '—'} ·{' '}
              {formatDuration(minutes)}
            </p>
            {shift.start_geo && (
              <GeoLine geo={shift.start_geo} label="начало смены" onPress={onGeoPress} />
            )}
          </div>
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Chip tone="success">смена завершена</Chip>
          <p className="text-text-muted mt-2 text-note">
            {shift.actual_start ? formatTime(shift.actual_start, timeZone) : '—'}–
            {shift.actual_end ? formatTime(shift.actual_end, timeZone) : '—'} ·{' '}
            {formatDuration(shift.minutes_total ?? 0)}
          </p>
          {shift.start_geo && (
            <GeoLine geo={shift.start_geo} label="начало смены" onPress={onGeoPress} />
          )}
          {shift.end_geo && (
            <GeoLine geo={shift.end_geo} label="конец смены" onPress={onGeoPress} />
          )}
          {shift.note && (
            <p className="mt-2 text-note leading-snug">{shift.note}</p>
          )}
        </div>
        <Money value={shift.amount} currency={currency} size="lg" />
      </div>
    </Card>
  )
}
